import { cloneDeep } from "lodash"
import { Track } from "../interfaces/api/content.api"

/**
 * Each entry has a required id and any number of additional
 * properties with string keys and values of any type.
 */
interface ISongQueueEntry {
    id: string
    title: string
    artist: string
    duration: number
    loading: number
    audioFormat?: "mp3"
    lyrics?: string
}

function toSongQueueEntry(
    track: Track,
    artist: string = "By me"
): ISongQueueEntry {
    return {
        id: track._id,
        title: track.name,
        artist,
        duration: track.duration,
        loading: track.loading,
        audioFormat: track.audioFormat ?? "mp3",
        ...(typeof track.lyrics === "string" ? { lyrics: track.lyrics } : {}),
    }
}

interface CurrentSong {
    song: ISongQueueEntry
    userQueueOrigin: boolean
}
/**
 * A generic class for managing a queue of songs or similar items, where each item has a unique ID.
 */
class SongQueue {
    private previous_recommended_queue: ISongQueueEntry[]
    private user_queue: ISongQueueEntry[]
    private next_recommended_queue: ISongQueueEntry[]
    private _current_song: CurrentSong | null = null

    constructor(
        userQueueEntries: ISongQueueEntry[] = [],
        nextRecommendedQueueEntries: ISongQueueEntry[] = [],
        currentSong: CurrentSong | null = null,
        previous_recommended_queue: ISongQueueEntry[] = []
    ) {
        this.previous_recommended_queue = [...previous_recommended_queue]
        this.user_queue = [...userQueueEntries]
        this.next_recommended_queue = [...nextRecommendedQueueEntries]

        if (currentSong !== null) {
            this._current_song = cloneDeep(currentSong)
        } else if (this.user_queue.length > 0) {
            this._current_song = {
                song: this.user_queue.shift(),
                userQueueOrigin: true,
            }
        } else if (this.next_recommended_queue.length > 0) {
            this._current_song = {
                song: this.next_recommended_queue.shift(),
                userQueueOrigin: false,
            }
        }
    }

    public get currentSong(): CurrentSong | null {
        if (this._current_song) {
            return cloneDeep(this._current_song)
        }

        if (this.user_queue.length > 0) {
            this._current_song = {
                song: this.user_queue.shift() as ISongQueueEntry,
                userQueueOrigin: true,
            }
        } else if (this.next_recommended_queue.length > 0) {
            this._current_song = {
                song: this.next_recommended_queue.shift() as ISongQueueEntry,
                userQueueOrigin: false,
            }
        } else {
            return null
        }

        return cloneDeep(this._current_song)
    }

    public set currentSong(song: CurrentSong | null) {
        this._current_song = song
    }

    public get nextSong(): ISongQueueEntry | null {
        if (this.user_queue.length > 0) {
            return cloneDeep(this.user_queue[0])
        } else if (this.next_recommended_queue.length > 0) {
            return cloneDeep(this.next_recommended_queue[0])
        } else {
            return null
        }
    }

    public get previousSong(): ISongQueueEntry | null {
        if (this.previous_recommended_queue.length > 0) {
            return cloneDeep(
                this.previous_recommended_queue[
                    this.previous_recommended_queue.length - 1
                ]
            )
        } else {
            return null
        }
    }

    public get previousQueue(): ISongQueueEntry[] {
        return cloneDeep(this.previous_recommended_queue)
    }

    public set previousQueue(queue: ISongQueueEntry[]) {
        this.previous_recommended_queue = cloneDeep(queue)
    }

    public get nextQueue(): ISongQueueEntry[] {
        return cloneDeep(this.next_recommended_queue)
    }

    public set nextQueue(queue: ISongQueueEntry[]) {
        this.next_recommended_queue = cloneDeep(queue)
        this.previous_recommended_queue = []
    }

    public get userQueue(): ISongQueueEntry[] {
        return cloneDeep(this.user_queue)
    }

    public set userQueue(queue: ISongQueueEntry[]) {
        this.user_queue = cloneDeep(queue)
    }

    public addToQueue(song: ISongQueueEntry, index?: number): void {
        if (typeof index === "undefined") {
            this.user_queue.push(song)
        } else if (
            typeof index === "number" &&
            index >= 0 &&
            index <= this.user_queue.length
        ) {
            this.user_queue.splice(index, 0, song)
        } else {
            throw new Error("Invalid index")
        }
    }

    public removeFromQueue(songID: string): void {
        this.user_queue = this.user_queue.filter(song => song.id !== songID)
    }

    public removeFromNextQueue(songID: string): void {
        this.next_recommended_queue = this.next_recommended_queue.filter(
            song => song.id !== songID
        )
    }

    private moveElementByIDAndIndex(
        id: string,
        index: number,
        queue: ISongQueueEntry[]
    ) {
        const songIndex = queue.findIndex(song => song.id === id)

        if (songIndex === -1) {
            console.warn("Song not found")
        }

        const [song] = queue.splice(songIndex, 1)
        queue.splice(index, 0, song)
    }

    public moveInQueue(songID: string, newIndex: number): void {
        this.moveElementByIDAndIndex(songID, newIndex, this.user_queue)
    }

    public moveInNextQueue(songID: string, newIndex: number): void {
        this.moveElementByIDAndIndex(
            songID,
            newIndex,
            this.next_recommended_queue
        )
    }

    public hasPreviousSong(): boolean {
        return (
            this.previous_recommended_queue.length > 0 ||
            (this.currentSong !== null && this.hasNextSong())
        )
    }

    public hasNextSong(): boolean {
        return (
            this.user_queue.length > 0 || this.next_recommended_queue.length > 0
        )
    }

    /**
     * Plays the next song in the queue. If there is no next song, returns null.
     * @returns The next song in the queue or null if there is no next song.
     */
    public playNext(): CurrentSong | null {
        const currentSongBefore: CurrentSong = cloneDeep(this._current_song)

        if (this.user_queue.length > 0) {
            this._current_song = {
                song: this.user_queue.shift() as ISongQueueEntry,
                userQueueOrigin: true,
            }
        } else if (this.next_recommended_queue.length > 0) {
            this._current_song = {
                song: this.next_recommended_queue.shift() as ISongQueueEntry,
                userQueueOrigin: false,
            }
        } else {
            return null
        }

        // If we skip a song, the old song will be added to the previous recommended
        // queue only if it was not taken from the user queue
        if (
            !currentSongBefore.userQueueOrigin &&
            currentSongBefore.song.id !== this._current_song.song.id
        ) {
            this.previous_recommended_queue.push(
                currentSongBefore.song as ISongQueueEntry
            )
        }

        return this._current_song
    }

    /**
     * Plays the previous song in the queue. If there is no previous song, returns null.
     * @returns The previous song in the queue or null if there is no previous song.
     */
    public playPrevious(): CurrentSong | null {
        const currentSongBefore: CurrentSong = cloneDeep(this._current_song)

        if (this.previous_recommended_queue.length > 0) {
            this._current_song = {
                song: this.previous_recommended_queue.pop() as ISongQueueEntry,
                userQueueOrigin: false,
            }

            // If the previous song was not taken from the user queue, add it back
            if (
                currentSongBefore.song.id !== this._current_song.song.id &&
                !currentSongBefore.userQueueOrigin
            ) {
                this.next_recommended_queue.unshift(
                    currentSongBefore.song as ISongQueueEntry
                )
            }
        }

        return this._current_song
    }

    /**
     * Plays a song immediately, placing it at the start of the queue and shifting the rest of the queue accordingly.
     * If the song is not already in the queue or if it is in the queue, it is moved to the front.
     * The current index is adjusted to point to this new song.
     * This mimics the bahavior of the Spotify queue.
     * @param song The song to play immediately.
     */
    public playImmediately(
        song: ISongQueueEntry,
        userQueueOrigin: boolean
    ): CurrentSong {
        this._current_song = {
            song: song,
            userQueueOrigin: userQueueOrigin,
        }

        const userQueueIndex = this.user_queue.findIndex(s => s.id === song.id)
        const recommendedQueueIndex = this.next_recommended_queue.findIndex(
            s => s.id === song.id
        )

        if (userQueueIndex !== -1) {
            // Remove all songs in the queue from the first one to the songIndex
            this.user_queue.splice(0, userQueueIndex)

            // TODO: do we need to add the songs to the previous recommended queue?
        }

        if (recommendedQueueIndex !== -1) {
            // Remove the song from the queue but keep the other songs intact
            this.next_recommended_queue.splice(recommendedQueueIndex, 1)
        }

        return this._current_song
    }

    // TODO: Implement corner cases depending on where the song was taken from (e.g. if taken from the recommended queue, remove it from there, etc.)

    /**
     * Moves a song to a new position in the queue.
     * @param songId The ID of the song to move.
     * @param newPosition The new position for the song.
     * @returns True if the song was moved successfully, false otherwise.
     */
    public moveSong(songId: string, newPosition: number): boolean {
        const index = this.getSongIndex(songId, this.user_queue)

        if (
            index > -1 &&
            newPosition >= 0 &&
            newPosition < this.user_queue.length
        ) {
            const [song] = this.user_queue.splice(index, 1)
            this.user_queue.splice(newPosition, 0, song)
            return true
        } else {
            return false
        }
    }

    /**
     * Find the index of the song with the given ID in the queue.
     * @param songId The ID of the song to find.
     * @returns The index of the song if found, -1 otherwise.
     */
    public getSongIndex(songId: string, queue: ISongQueueEntry[]): number {
        return queue.findIndex(song => song.id === songId)
    }

    /**
     * Gets a song by its index in the queue.
     * @param index The index of the song.
     * @returns The song if the index is valid, undefined otherwise.
     */
    public getSongByIndex(
        index: number,
        queue: ISongQueueEntry[]
    ): ISongQueueEntry | undefined {
        if (index >= 0 && index < queue.length) {
            return { ...queue[index] } // Return a copy
        } else {
            return undefined
        }
    }

    /**
     * Removes a song from the queue by its ID.
     * @param songId The ID of the song to remove.
     * @returns True if the song was removed, false if the song was not found.
     */
    public removeSongById(songId: string, queue: ISongQueueEntry[]): boolean {
        const index = this.getSongIndex(songId, queue)

        if (index > -1) {
            queue.splice(index, 1)
            return true
        } else {
            return false
        }
    }

    /**
     * Clears the queue of songs.
     */
    public clearQueue(): void {
        this.user_queue = []
    }

    public clearAllQueues(): void {
        this.previous_recommended_queue = []
        this.user_queue = []
        this.next_recommended_queue = []
    }
}

export { SongQueue, ISongQueueEntry, CurrentSong, toSongQueueEntry }
